import { useResponsive, useScrollPosition } from '@/hooks';
import { AdvancedSettingsAddress, AdvancedSettingsAiFeatures, AdvancedSettingsClientPortal,AdvancedSettingsModules, AdvancedSettingsSecurity, AdvancedSettingsAppearance, AdvancedSettingsNotifications, AdvancedSettingsPreferences, AuthEmail, AuthPassword, AuthSingleSingOn, AuthSocialSignIn, AuthTwoFactor, BasicSettings, ResetPreferences, ExternalServicesIntegrations, ExternalServicesManageApi } from './blocks';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Scrollspy } from '@/components/scrollspy/Scrollspy';
import { AccountSettingsSidebar } from '@/pages/account/home/settings-sidebar';
import { useLayout } from '@/providers';
import companySettings from '@/services/setting/companySettings';
import { toast } from 'sonner';
import { useAuthContext } from '@/auth';
import { isSuperAdmin } from '@/utils';
import { pickSecurity } from './blocks/securitySwitches';

const stickySidebarClasses = {
  'demo1-layout': 'top-[calc(var(--tw-header-height)+1rem)]',
  'main-layout': 'top-[calc(var(--tw-header-height)+1rem)]',
  'demo3-layout': 'top-[calc(var(--tw-header-height)+var(--tw-navbar-height)+1rem)]',
  'demo4-layout': 'top-[3rem]',
  'demo5-layout': 'top-[calc(var(--tw-header-height)+1.5rem)]',
  'demo6-layout': 'top-[3rem]',
  'demo7-layout': 'top-[calc(var(--tw-header-height)+1rem)]',
  'demo8-layout': 'top-[3rem]',
  'demo9-layout': 'top-[calc(var(--tw-header-height)+1rem)]',
  'demo10-layout': 'top-[1.5rem]'
};
const AccountSettingsSidebarContent = () => {
  const desktopMode = useResponsive('up', 'lg');
  const { currentLayout } = useLayout();
  const [sidebarSticky, setSidebarSticky] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null)
  const [compayData, setCompayData] = useState(null);
  const [basicData, setBasicData] = useState(null);
  const [addressData, setAddressData] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  // Initialize ref for parentEl
  const parentRef = useRef(document); // Default to document
  const scrollPosition = useScrollPosition({
    targetRef: parentRef
  });
  const { auth, updateUser } = useAuthContext();

  const getProfileSettings = async () => {
    try {
      setLoading(true);
      const { data, status } = isSuperAdmin() 
        ? await companySettings.superAdminGetCompanySettings() 
        : await companySettings.getCompanySettings();
      if (status) {
        const updatedUser = {
          hubspot_integration: data.integration_hubspot,
          company_name: data.legal_business_name,
          company_logo: data.company_logo,
          // Keep the stored session in sync so the Aila panel reacts to the
          // AI Features toggle without a re-login (missing key = on).
          ai_features_enabled: data.ai_features_enabled !== false,
          // Same for the Modules switches: the nav reads this list (OL-135).
          hidden_modules: Array.isArray(data.hidden_modules) ? data.hidden_modules : [],
          // And the firm safeguards, e.g. export approval (OL-152).
          ...(isSuperAdmin() ? {} : { security: pickSecurity(data) })
        }
        updateUser(updatedUser, true)
        setCompayData(data);
        const {
          address,
          address_2,
          city,
          country,
          zip_postal_code,
          firm_email,
          date_format,
          time_format,
          ...restBasicData
        } = data;
        setAddressData({
          address: data.address,
          address_2: data.address_2,
          city: data.city,
          country: data.country,
          zip_postal_code: data.zip_postal_code,
        });
        setEmailData({
          firm_email: data.firm_email,
        });
        setBasicData(restBasicData);

      }
    } catch (error) {
      console.error('Error:', error)
      setLoading(false);
    } finally {
      setLoading(false)
    }
  }

  function prettifyField(key) {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function extractValidationMessages(error) {
    const errs =
      error?.response?.data?.errors ||
      error?.errors ||
      {};

    const messages = [];
    Object.entries(errs).forEach(([field, msgs]) => {
      const label = prettifyField(field);
      (Array.isArray(msgs) ? msgs : [String(msgs)]).forEach((m) => {
        messages.push(`${label}: ${m}`);
      });
    });
    return messages;
  }

  const updateProfileSettings = async (formData) => {
    try {
      setSaveLoading(true);
      const { data, status } = isSuperAdmin() 
        ? await companySettings.superAdminUpdateCompanySettings(formData) 
        : await companySettings.updateCompanySettings(formData);
      if (status) {
        getProfileSettings()
        toast.success('Company Settings Updated Successfully', {
          description: `Company Profile Settings Information Updated Successfully.`,

        });
      }
    } catch (error) {
      console.error('Error:', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const lines = extractValidationMessages(error);

        toast.warning('Validation Error', {
          description: (
            <div>
              <ul className=" space-y-1 text-xs">
                {lines.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          ),
        });

        return;
      }
      toast.error('Failed to update company settings.', {
        description: `An error occurred while updating company settings information. Please try again later.`,

      });
      setSaveLoading(false);
    } finally {
      setSaveLoading(false)
    }
  }

  const restoreCompanySettings = async () => {
    try {
      setRestoreLoading(true);
      const { data, status } = isSuperAdmin() 
        ? await companySettings.superAdminRestoreDefaultSettings() 
        : await companySettings.restoreDefaultSettings();
      if (status) {
        getProfileSettings()
        setUserData(data)
        toast.success('Company Settings Restored', {
          description: `Company Profile Settings Restored Successfully.`,

        });
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error('Failed to restore default settings', {
        description: `An error occurred while restoring default settings. Please try again later.`,

      });
      setRestoreLoading(false);
    } finally {
      setRestoreLoading(false)
    }
  }

  // Effect to update parentRef after the component mounts
  useEffect(() => {
    const scrollableElement = document.getElementById('scrollable_content');
    if (scrollableElement) {
      parentRef.current = scrollableElement;
    }
  }, []);

  // Handle scroll position and sidebar stickiness
  useEffect(() => {
    setSidebarSticky(scrollPosition > 100);
  }, [scrollPosition, currentLayout?.options]);

  useEffect(() => {
    getProfileSettings()
  }, [])

  // Get the sticky class based on the current layout, provide a default if not found
  const stickyClass = currentLayout?.name ? stickySidebarClasses[currentLayout.name] || 'top-[calc(var(--tw-header-height)+1rem)]' : 'top-[calc(var(--tw-header-height)+1rem)]';
  return <div className="flex grow gap-5 lg:gap-7.5">
    {desktopMode && <div className="w-[230px] shrink-0">
      <div className={clsx('w-[230px]', sidebarSticky && `fixed z-10 start-auto top-[calc(var(--tw-header-height)+1rem)]`)}> {/*${stickyClass} */}
        <Scrollspy offset={100} targetRef={parentRef}>
          <AccountSettingsSidebar />
        </Scrollspy>
      </div>
    </div>}

    <div className="flex flex-col items-stretch grow gap-5 lg:gap-7.5">
      <BasicSettings
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading}
      />

      <AuthEmail
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading}
      />

      <AdvancedSettingsAddress
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading}
      />

      {/* <AuthPassword /> */}

      {/* <AuthSocialSignIn /> */}

      {/* <AuthSingleSingOn /> */}

      <AuthTwoFactor />

      <AdvancedSettingsPreferences
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading} />

      <AdvancedSettingsAiFeatures
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading}
      />

      <AdvancedSettingsModules
        data={compayData}
        onConfirm={(formData) => updateProfileSettings(formData)}
        loadData={loading}
        loadDataSave={saveLoading}
      />

      {!isSuperAdmin() && (
        <AdvancedSettingsClientPortal
          data={compayData}
          onConfirm={(formData) => updateProfileSettings(formData)}
          loadData={loading}
          loadDataSave={saveLoading}
        />
      )}

      {!isSuperAdmin() && (
        <AdvancedSettingsSecurity
          data={compayData}
          onConfirm={(formData) => updateProfileSettings(formData)}
          loadData={loading}
          loadDataSave={saveLoading}
        />
      )}

      <AdvancedSettingsAppearance title={''} />

      {/* <AdvancedSettingsNotifications /> */}

      {/* <ExternalServicesManageApi title={''} switch={false} /> */}

      <ExternalServicesIntegrations />

      <ResetPreferences onConfirm={restoreCompanySettings} restoreLoading={restoreLoading} />
    </div>
  </div>;
};
export { AccountSettingsSidebarContent };